<script setup lang="ts">
  import { inject, ref, watch } from 'vue';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { formatDateToPtBR } from '@/shared/lib';
  import { ColoredButton, DatePicker, IconImage, NumberStepperInput } from '@/shared/ui';
  import {
    AddRecordFilterInjectionKey,
    createAddRecordFilterContext,
    type AddRecordFilters,
  } from '../model/addRecordFilterContext';

  const props = withDefaults(
    defineProps<{
      currentFilters?: AddRecordFilters;
    }>(),
    {
      currentFilters: () => ({}),
    }
  );

  const { isFilterPanelOpen } = inject(
    AddRecordFilterInjectionKey,
    () => createAddRecordFilterContext(),
    true
  );

  const emit = defineEmits<{
    (e: 'clear'): void;
    (e: 'apply', filters: AddRecordFilters): void;
  }>();

  const selectedDate = ref<Date | null>(null);
  const isDateOpen = ref(false);
  const revision = ref<number | null>(0);

  function resetDraft(): void {
    selectedDate.value = props.currentFilters.createdAt ?? null;
    revision.value = props.currentFilters.revision ?? 0;
    isDateOpen.value = false;
  }

  watch(() => props.currentFilters, resetDraft, { immediate: true });

  watch(isFilterPanelOpen, (isOpen) => {
    if (!isOpen) {
      resetDraft();
    }
  });

  function handleClear(): void {
    selectedDate.value = null;
    revision.value = 0;
    isDateOpen.value = false;
    emit('clear');
  }

  function handleApply(): void {
    emit('apply', {
      createdAt: selectedDate.value,
      revision: revision.value && revision.value > 0 ? revision.value : null,
    });
  }
</script>

<template>
  <div
    data-testid="add-record-filter-panel"
    class="flex flex-col gap-6 px-6 pt-4 pb-5 text-left"
  >
    <div class="flex flex-wrap items-start gap-6">
      <div class="flex shrink-0 flex-col gap-1.5">
        <label
          for="add-record-filter-date"
          class="text-sm font-medium text-gray-07"
        >
          Data de Vigência
        </label>
        <button
          id="add-record-filter-date"
          type="button"
          data-testid="add-record-filter-date"
          class="flex h-10.5 w-[204px] items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 text-sm font-medium text-gray-03 focus:border-(--theme-secondary-ink) focus:outline-none"
          @click="isDateOpen = true"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-5 w-5 shrink-0"
          />
          <span class="truncate text-left">
            {{ selectedDate ? formatDateToPtBR(selectedDate) : 'Selecione uma data' }}
          </span>
        </button>
        <DatePicker
          v-model:selected-date="selectedDate"
          v-model:is-open="isDateOpen"
          :default-to-today="false"
          @apply="isDateOpen = false"
          @cancel="isDateOpen = false"
        />
      </div>

      <div class="flex shrink-0 flex-col gap-1.5">
        <label
          for="add-record-filter-revision"
          class="text-sm font-medium text-gray-07"
        >
          Revisão
        </label>
        <NumberStepperInput
          input-id="add-record-filter-revision"
          :model-value="revision"
          :min="0"
          :step="1"
          input-class="h-10.5 py-0"
          @update:model-value="revision = $event"
        />
      </div>
    </div>

    <div class="flex justify-end gap-4">
      <ColoredButton
        text="Cancelar"
        outlined
        color="gray-03"
        border-color="gray-03"
        data-testid="add-record-filter-cancel"
        @click="handleClear"
      />
      <ColoredButton
        text="Aplicar filtros"
        data-testid="add-record-filter-apply"
        @click="handleApply"
      />
    </div>
  </div>
</template>
